import {
  AgentStudioDraftPayload,
  Course,
  CourseAuthoringItem,
  CourseModuleAuthoring,
  CreateCoursePayload,
  CreateLessonPayload,
  CreateModulePayload,
  Enrollment,
  LearnerHistoryResponse,
  Lesson,
  LessonAuthoring,
  LessonProgress,
  PaginatedResult,
  Quiz,
  QuizAttemptResult,
  User,
} from './types';

const TOKEN_KEY = 'learnos_jwt_token';

export const tokenStorage = {
  get: (): string | null => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string): void => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      // storage unavailable
    }
  },
  clear: (): void => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // storage unavailable
    }
  },
};

async function request<T>(
  path: string,
  options: RequestInit = {},
  config: { skipToken?: boolean; token?: string } = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (config.token) {
    headers['Authorization'] = `Bearer ${config.token}`;
  } else if (!config.skipToken) {
    const token = tokenStorage.get();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorDetail = 'An error occurred';
    try {
      const errJson = await response.json();
      errorDetail = errJson.message || errJson.error || JSON.stringify(errJson);
    } catch {
      errorDetail = response.statusText || `HTTP ${response.status}`;
    }
    const error: any = new Error(errorDetail);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export const apiClient = {
  auth: {
    async login(identifier: string, secret: string): Promise<{ accessToken: string }> {
      const res = await request<{ accessToken: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: identifier.includes('@') ? identifier : undefined,
          username: !identifier.includes('@') ? identifier : undefined,
          password: secret,
        }),
      });
      tokenStorage.set(res.accessToken);
      return res;
    },
    async redeemSso(code: string): Promise<{ success: boolean; user: User }> {
      return request<{ success: boolean; user: User }>('/api/auth/sso/redeem', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
    },
    async logout(): Promise<void> {
      try {
        await request('/api/auth/logout', { method: 'POST' });
      } catch {
        // Ignore network failure during logout
      } finally {
        tokenStorage.clear();
      }
    },
  },

  me: {
    async getProfile(options?: { skipToken?: boolean; token?: string }): Promise<User> {
      return request<User>('/api/me', {}, options);
    },
    async getEnrollments(): Promise<Enrollment[]> {
      return request<Enrollment[]>('/api/me/enrollments');
    },
    async getProgress(): Promise<LessonProgress[]> {
      return request<LessonProgress[]>('/api/me/progress');
    },
    async getHistory(): Promise<LearnerHistoryResponse> {
      return request<LearnerHistoryResponse>('/api/me/history');
    },
  },

  catalog: {
    async listCourses(params?: {
      page?: number;
      limit?: number;
      search?: string;
    }): Promise<PaginatedResult<Course>> {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', params.page.toString());
      if (params?.limit) query.set('limit', params.limit.toString());
      if (params?.search) query.set('search', params.search);
      const queryStr = query.toString();
      return request<PaginatedResult<Course>>(`/api/catalog/courses${queryStr ? '?' + queryStr : ''}`);
    },

    async getCourse(courseId: string): Promise<Course> {
      return request<Course>(`/api/catalog/courses/${courseId}`);
    },

    async getLesson(courseId: string, lessonId: string): Promise<Lesson> {
      return request<Lesson>(`/api/catalog/courses/${courseId}/lessons/${lessonId}`);
    },

    async selfEnroll(courseId: string): Promise<Enrollment> {
      return request<Enrollment>(`/api/catalog/courses/${courseId}/self-enroll`, {
        method: 'POST',
      });
    },
  },

  progress: {
    async recordLessonProgress(
      courseId: string,
      lessonId: string,
      data: { completed: boolean; lastPositionSeconds?: number }
    ): Promise<LessonProgress> {
      return request<LessonProgress>(
        `/api/enrollments/courses/${courseId}/lessons/${lessonId}/progress`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },
  },

  quizzes: {
    async getQuiz(quizId: string): Promise<Quiz> {
      return request<Quiz>(`/api/quizzes/${quizId}`);
    },

    async submitAttempt(
      quizId: string,
      answers: { questionId: string; selectedOptionIds?: string[]; selectedOptionId?: string }[]
    ): Promise<QuizAttemptResult> {
      return request<QuizAttemptResult>(`/api/quizzes/${quizId}/attempts`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      });
    },
  },

  authoring: {
    async listCourses(orgId: string): Promise<PaginatedResult<CourseAuthoringItem>> {
      return request<PaginatedResult<CourseAuthoringItem>>(`/api/organizations/${orgId}/courses`);
    },

    async getCourse(orgId: string, courseId: string): Promise<CourseAuthoringItem> {
      return request<CourseAuthoringItem>(`/api/organizations/${orgId}/courses/${courseId}`);
    },

    async createCourse(orgId: string, data: CreateCoursePayload): Promise<CourseAuthoringItem> {
      return request<CourseAuthoringItem>(`/api/organizations/${orgId}/courses`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async addModule(orgId: string, courseId: string, data: CreateModulePayload): Promise<CourseModuleAuthoring> {
      return request<CourseModuleAuthoring>(`/api/organizations/${orgId}/courses/${courseId}/modules`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async addLesson(orgId: string, moduleId: string, data: CreateLessonPayload): Promise<LessonAuthoring> {
      return request<LessonAuthoring>(`/api/organizations/${orgId}/courses/modules/${moduleId}/lessons`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async publishCourse(orgId: string, courseId: string): Promise<CourseAuthoringItem> {
      return request<CourseAuthoringItem>(`/api/organizations/${orgId}/courses/${courseId}/publish`, {
        method: 'POST',
      });
    },

    async archiveCourse(orgId: string, courseId: string): Promise<CourseAuthoringItem> {
      return request<CourseAuthoringItem>(`/api/organizations/${orgId}/courses/${courseId}/archive`, {
        method: 'POST',
      });
    },

    async importAgentStudioDraft(orgId: string, data: AgentStudioDraftPayload): Promise<CourseAuthoringItem> {
      return request<CourseAuthoringItem>(`/api/organizations/${orgId}/courses/agent-studio-draft`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },
};
